import { ComponentRef, inject, Injectable, Type } from '@angular/core';
import { AbstractService } from './abstract-service';
import { PreferenceService } from './preference.service';
import { SolSettingKey } from '../models/core/sol-setting-key';

export interface DetailState {
  detail?: SolDetail<any>;
  fixed: boolean;
  width: number;
}

export interface SolDetail<T> {
  detailId: string;
  componentType: Type<T>;
  configurer: (componentRef: ComponentRef<T>) => void;
  onClose: () => boolean;
  onAction: (actionId: string) => void;
  mode: SolDetailMode;
  closeable: boolean;
}

export interface SolDetailOptions {
  visibility: SolDetailVisibility;
  closeFn: () => boolean;
  onAction: (actionId: string) => void;
  mode: SolDetailMode;
  closeable: boolean;
}

export interface SolDetailRef {
  close(): void;
}

/**
 * Determines whether a detail panel should be shown always or only update the existing one:
 * - default: Always create a new detail panel.
 * - update: Only update the existing detail panel if it is already shown.
 */
export type SolDetailVisibility = 'default' | 'update';

/**
 * Layout mode of the detail panel:
 * - default: honour the user's persisted fixed/floating preference; toggle visible.
 * - floating: force floating layout; toggle hidden.
 * - fixed: force fixed (docked) layout; toggle hidden.
 */
export type SolDetailMode = 'default' | 'floating' | 'fixed';

@Injectable({
  providedIn: 'root',
})
export class DetailService extends AbstractService<DetailState> {
  public static readonly minWidth = 450;
  public static readonly defaultWidth = 450;

  // Dependencies
  private readonly preferenceService = inject(PreferenceService);

  constructor() {
    super({
      fixed: false,
      width: DetailService.defaultWidth,
    });
    this.patch({
      fixed: this.preferenceService.cached(SolSettingKey.detailFixed) ?? false,
      width: this.preferenceService.cached(SolSettingKey.detailWidth) ?? DetailService.defaultWidth,
    });
    this.reconcile();
  }

  public show<T>(
    detailId: string,
    componentType: Type<T>,
    configurer: (componentRef: ComponentRef<T>) => void,
    options: Partial<SolDetailOptions> = {},
  ): SolDetailRef {
    const visibility = options?.visibility ?? 'default';
    if (visibility === 'update' && this.state.detail === undefined) {
      return {
        close: () => {},
      };
    }

    const onClose = () => (options?.closeFn ? options.closeFn() : true);
    const onAction = options?.onAction ? options.onAction : () => {};
    const mode = options?.mode ?? 'default';
    const closeable = options?.closeable ?? true;
    const detail = { detailId, componentType, configurer, onClose, onAction, mode, closeable };
    this.patch({ detail });
    return {
      close: () => this.close(),
    };
  }

  public close(): void {
    const detail = this.state.detail;
    if (detail?.closeable === false || (detail !== undefined && !detail.onClose())) {
      return;
    }
    this.patch({
      detail: undefined,
    });
  }

  /** Closes the panel shown under `detailId`, without calling its `closeFn`; another panel in the slot stays. */
  public remove(detailId: string): void {
    if (this.state.detail?.detailId === detailId) {
      this.patch({ detail: undefined });
    }
  }

  public forceClose(): void {
    this.patch({
      detail: undefined,
    });
  }

  public toggleFixed(): void {
    this.setFixed(!this.state.fixed);
  }

  public setFixed(fixed: boolean): void {
    const previous = this.state.fixed;
    this.patch({ fixed });
    this.preferenceService.save(SolSettingKey.detailFixed, fixed, { announcedAsToast: true }).subscribe({
      error: () => this.patch({ fixed: previous }),
    });
  }

  public setWidth(width: number): void {
    const previous = this.state.width;
    const bounded = Math.max(width, DetailService.minWidth);
    this.patch({ width: bounded });
    this.preferenceService.save(SolSettingKey.detailWidth, bounded, { announcedAsToast: true }).subscribe({
      error: () => this.patch({ width: previous }),
    });
  }

  private reconcile(): void {
    this.preferenceService.load(SolSettingKey.detailFixed).subscribe(fixed => {
      if (fixed !== undefined) {
        this.patch({ fixed });
      }
    });
    this.preferenceService.load(SolSettingKey.detailWidth).subscribe(width => {
      if (width !== undefined) {
        this.patch({ width });
      }
    });
  }
}
